/**
 * Navigation principale de l'application, PARTAGÉE entre la barre latérale
 * (App.vue) et la recherche globale (GlobalSearch.vue) — une seule source de
 * vérité pour les pages, leurs libellés, leurs icônes et leur visibilité par
 * rôle.
 *
 * Les items sont organisés en GROUPES pour la sidebar (quotidien, outils,
 * admin, analyse, application) : moins intimidant qu'une longue liste plate.
 * `keywords` alimente la recherche globale avec des synonymes du langage
 * courant (« blacklist », « rdv », « annuaire »…).
 */
import { computed } from 'vue'
import { useAuthStore } from '@/shared/stores/auth'

export interface NavItem {
  to: string
  icon: string
  label: string
  exact: boolean
  show: boolean
  /** Synonymes pour la recherche globale (jamais affichés). */
  keywords?: string[]
}

export interface NavGroup {
  /** Libellé au-dessus du groupe. Absent = groupe principal (quotidien). */
  label?: string
  items: NavItem[]
}

export function useNav() {
  const auth = useAuthStore()

  const groups = computed<NavGroup[]>(() => {
    const defs: NavGroup[] = [
      {
        // Le quotidien de l'agent : appeler et suivre ses résultats.
        items: [
          { to: '/', icon: 'home', label: 'Tableau de bord', exact: true, show: true, keywords: ['accueil', 'dashboard', 'résumé'] },
          { to: '/queue', icon: 'inbox', label: auth.actingAsAgent ? "Espace d'appel" : 'Gestion des appels', exact: false, show: true, keywords: ['file', 'appeler', 'numéros', 'campagne', 'session'] },
          { to: '/callbacks', icon: 'clock', label: 'Rappels', exact: false, show: true, keywords: ['rappeler', 'callback', 'à rappeler'] },
          { to: '/planning', icon: 'grid', label: 'Mon planning', exact: false, show: true, keywords: ['horaires', 'pauses', 'emploi du temps', 'heures', 'validation'] },
          { to: '/appointments', icon: 'calendar', label: 'Rendez-vous', exact: false, show: true, keywords: ['rdv', 'agenda'] },
          { to: '/sales', icon: 'briefcase', label: 'Ventes', exact: false, show: true, keywords: ['vente', "chiffre d'affaires"] },
        ],
      },
      {
        label: 'Outils',
        items: [
          // « Lignes » : choix du trunk sortant actif. Affiché seulement si
          // l'agent a au moins 2 lignes assignées (sinon rien à choisir).
          { to: '/lines', icon: 'phone-outgoing', label: 'Lignes', exact: false, show: (auth.user?.lines_count ?? 0) > 1, keywords: ['trunk', 'ligne sortante', 'opérateur'] },
          { to: '/repertoire', icon: 'phone', label: 'Répertoire', exact: false, show: true, keywords: ['annuaire', 'collègues', 'postes', 'appel interne'] },
          { to: '/chat', icon: 'message-circle', label: 'Messages', exact: false, show: true, keywords: ['chat', 'messagerie', 'discussion', 'groupe'] },
          { to: '/scripts', icon: 'book-open', label: 'Scripts', exact: false, show: true, keywords: ['argumentaire', 'texte'] },
          { to: '/cdr', icon: 'list', label: 'Historique', exact: false, show: true, keywords: ['appels passés', 'journal', 'enregistrements', 'écouter'] },
          { to: '/recordings', icon: 'star', label: 'Favoris', exact: false, show: true, keywords: ['enregistrements favoris', 'audio conservé'] },
        ],
      },
      {
        label: 'Admin',
        items: [
          { to: '/supervisor', icon: 'eye', label: 'Supervision', exact: false, show: auth.isTeamAdmin, keywords: ['live', 'écoute', 'agents en ligne', 'temps réel'] },
          { to: '/campaigns', icon: 'target', label: 'Campagnes', exact: false, show: auth.isTeamAdmin, keywords: ['fiches', 'numéroteur', 'projets'] },
          { to: '/number-lists', icon: 'hash', label: 'Listes de numéros', exact: false, show: auth.isAdmin, keywords: ['import', 'fichier', 'pioche', 'csv'] },
          { to: '/contacts-hub', icon: 'users', label: 'Contacts', exact: false, show: auth.isAdmin, keywords: ['carnet', 'clients', 'numéros', 'google'] },
          { to: '/follow-up', icon: 'user-check', label: "Suivi d'appels", exact: false, show: auth.isAdmin, keywords: ['recouvrement', 'fidélisation', 'validation', 'revue'] },
          { to: '/forms', icon: 'file-text', label: 'Formulaires', exact: false, show: auth.isTeamAdmin, keywords: ['champs', 'questionnaire'] },
          { to: '/users', icon: 'user-plus', label: 'Utilisateurs', exact: false, show: auth.canManageUsers, keywords: ['comptes', 'équipes', 'agents', 'rôles', 'horaires'] },
          { to: '/telephony', icon: 'server', label: 'Téléphonie', exact: false, show: auth.isAdmin, keywords: ['opérateurs', 'trunk', 'sip', 'numéros entrants', 'numéro présenté'] },
          { to: '/ring-groups', icon: 'phone-incoming', label: 'Distribution', exact: false, show: auth.isAdmin, keywords: ['entrants', 'groupes de sonnerie', 'ring group'] },
          { to: '/blacklist', icon: 'x-circle', label: 'Liste noire', exact: false, show: auth.isAdmin, keywords: ['blacklist', 'bloquer', 'ne plus appeler'] },
          { to: '/call-flows', icon: 'git-branch', label: 'Automatisation', exact: false, show: auth.isAdmin, keywords: ['call flow', 'svi', 'scénario', 'flux'] },
        ],
      },
      {
        label: 'Analyse',
        items: [
          { to: '/statistics', icon: 'bar', label: 'Statistiques', exact: false, show: auth.isTeamAdmin, keywords: ['dispositions', 'résultats', 'export', 'fiche qualifiée'] },
          { to: '/agent-time', icon: 'activity', label: 'Temps agents', exact: false, show: auth.isTeamAdmin, keywords: ['heures', 'connexion', 'présence', 'pauses'] },
          { to: '/performance', icon: 'trending-up', label: 'Performance', exact: false, show: auth.isTeamAdmin, keywords: ['score', 'graphes', 'conversion', 'qualité des listes'] },
          { to: '/qualified-list', icon: 'download', label: 'Liste qualifiée', exact: false, show: auth.canSeeQualifiedList, keywords: ['export', 'csv', 'excel', 'appels répondus', 'contacts qualifiés'] },
        ],
      },
      {
        label: 'Application',
        items: [
          { to: '/aide', icon: 'help-circle', label: 'Aide', exact: false, show: true, keywords: ['documentation', 'guide', 'question', 'comment faire'] },
          { to: '/settings', icon: 'settings', label: 'Paramètres', exact: false, show: true, keywords: ['réglages', 'audio', 'micro', 'compte', 'configuration'] },
        ],
      },
    ]
    return defs
      .map((g) => ({ ...g, items: g.items.filter((i) => i.show) }))
      .filter((g) => g.items.length > 0)
  })

  /** Tous les items visibles, à plat — pour la recherche globale. */
  const flatItems = computed<NavItem[]>(() => groups.value.flatMap((g) => g.items))

  return { groups, flatItems }
}
